import { useState } from 'react';
import { Form, Formik, Field, FormikErrors } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import {
  LoadableButton,
  Input,
  Text,
  Label,
  Flex,
  Box
} from '../../components';
import messages from './messages';
import {
  validateValueCodeQuery,
  validateValueCodeQueryVariables
} from '../../core-types';
import { gql } from '@apollo/client';
import { GraphQLErrors } from '../../components/GraphQLErrors';

const VALIDATE_VALUE_CODE_QUERY = gql`
  query validateValueCodeQuery($valueCode: String!) {
    validateValueCode(valueCode: $valueCode) {
      id
      slug {
        current
      }
    }
  }
`;

type Props = {
  onValidCouponEntered: (obj: {
    validCoupon: string;
    productId: string | null;
  }) => void;
};

const VALUE_CODE_LENGTH = 6;

type Values = { valueCode: string };

const CheckCouponForm = ({ onValidCouponEntered }: Props) => {
  const [validCoupon, setValidCoupon] = useState<string>('');
  const [validateValueCode, { loading, error }] = useLazyQuery<
    validateValueCodeQuery,
    validateValueCodeQueryVariables
  >(VALIDATE_VALUE_CODE_QUERY, {
    errorPolicy: 'none',
    onCompleted: data => {
      onValidCouponEntered({
        validCoupon,
        productId: data.validateValueCode && data.validateValueCode.id
      });
    }
  });
  return (
    <Formik<Values>
      initialValues={{
        valueCode: ''
      }}
      isInitialValid={false}
      validate={({ valueCode }) => {
        const errors: FormikErrors<Values> = {};
        if (valueCode.length !== VALUE_CODE_LENGTH) {
          errors.valueCode = 'INVALID_VALUE_CODE_LENGTH';
        }
        return errors;
      }}
      onSubmit={async values => {
        setValidCoupon(values.valueCode);
        validateValueCode({
          variables: {
            valueCode: values.valueCode
          }
        });
      }}
    >
      {({ isValid }) => {
        return (
          <Form>
            {error && <GraphQLErrors error={error} />}
            <Box ml="auto" mr="auto" maxWidth="250px">
              <Label mb={5} textAlign="left">
                <Text mute typoStyle="xxs" mb="xxs">
                  <FormattedMessage {...messages.valueCodeLabel} />
                </Text>
                <Field name="valueCode">
                  {({ field }) => {
                    return (
                      <Input {...field} data-testid="RedeemCoupon__Input" />
                    );
                  }}
                </Field>
              </Label>
              <Flex justifyContent="center">
                <LoadableButton
                  type="submit"
                  variant="primary"
                  shape="cool"
                  loading={loading}
                  responseState={error ? 'error' : 'success'}
                  disabled={!isValid}
                  data-testid="RedeemCoupon__Button"
                >
                  <FormattedMessage id="redeem" defaultMessage="Redeem" />
                </LoadableButton>
              </Flex>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CheckCouponForm;
